import { telemetry } from './utils/telemetry';
import { spatialEventBus } from '@hdri/src/spatialSync/spatialEventBus';

/**
 * ATOM W-006: Shader Interaction Governance
 * 
 * Manages orb shader states, interaction cooldowns, and rendering
 * continuity during cinematic camera movements.
 */
class ShaderGovernance {
    private lastInteractionTime: number = 0;
    private cooldownDuration: number = 2000; // 2s cooldown
    private shaderMode: 'HIGH_QUALITY' | 'PERFORMANCE' = 'HIGH_QUALITY';
    private gesturePriority: 'ORB_PRIMARY' | 'MAP_PRIMARY' = 'ORB_PRIMARY';

    constructor() {
        if (typeof window !== 'undefined') {
            window.addEventListener('spatial:flyto_start', () => this.setPerformanceMode(true));
            window.addEventListener('spatial:flyto_end', () => this.setPerformanceMode(false));
            
            // Register global access for components and tests
            (window as any).WingmanShaderGovernance = this;
            telemetry.info('GOVERNANCE', 'SHADER_GOVERNANCE_INITIALIZED');
        }
    }

    /**
     * Record a USER interaction with the orb/globe.
     */
    public recordInteraction() {
        this.lastInteractionTime = Date.now();
        telemetry.forensic('GOVERNANCE', 'INTERACTION_RECORDED', { timestamp: this.lastInteractionTime });
    }

    /**
     * Checks if a cooldown is currently active.
     */
    public hasCooldownActive(): boolean {
        return (Date.now() - this.lastInteractionTime) < this.cooldownDuration;
    }

    /**
     * Checks if it's safe to resume cinematic drift or heavy animations.
     */
    public isSafeToInteract(): boolean {
        const lockOwner = spatialEventBus.getLockOwner();
        // If USER holds the lock, they are interacting
        return lockOwner !== 'USER' || !this.hasCooldownActive();
    }

    /**
     * ATOM S1.5 — Cold Boot Purge
     * Deterministically reset all governance state.
     */
    public purgeAll() {
        this.lastInteractionTime = 0;
        this.shaderMode = 'HIGH_QUALITY';
        telemetry.info('GOVERNANCE', 'PURGE_ALL_COMPLETE');
    }

    /**
     * Restore shader to default state (High Quality, no interaction locks).
     */
    public restore() {
        this.lastInteractionTime = 0;
        this.shaderMode = 'HIGH_QUALITY';
        telemetry.info('GOVERNANCE', 'RESTORE_COMPLETE');
    }

    /**
     * Forensic Visibility: Check if FlyTo is currently active.
     */
    public isFlyToActive(): boolean {
        return this.shaderMode === 'PERFORMANCE';
    }

    /**
     * Forensic Visibility: Check if Interaction Cooldown is active.
     */
    public isInteractionCoolingDown(): boolean {
        return this.hasCooldownActive();
    }

    /**
     * Set shader performance mode (e.g. during high-speed FlyTo).
     */
    public setPerformanceMode(active: boolean) {
        this.shaderMode = active ? 'PERFORMANCE' : 'HIGH_QUALITY';
        telemetry.info('GOVERNANCE', 'SHADER_MODE_CHANGED', { 
            mode: this.shaderMode,
            source: active ? 'FLYTO_START' : 'FLYTO_END'
        });
    }

    public getShaderMode() {
        return this.shaderMode;
    }

    public canUserOverride(): boolean {
        const lockOwner = spatialEventBus.getLockOwner();
        // USER can always override, but we log if it breaks an AI sequence
        if (lockOwner === 'AI') {
            telemetry.warn('GOVERNANCE', 'USER_OVERRIDE_AI_LOCK');
        }
        return true;
    }

    public getGesturePriority() {
        return this.gesturePriority;
    }

    /**
     * Calculates a smooth alpha/intensity multiplier based on governance state.
     * 🛡️ ATOM W-010: Clamped to minimum 0.35 to prevent shader disappearance.
     */
    public getIntensityMultiplier(): number {
        // Drop intensity during high-speed moves for performance/visual clarity
        if (this.shaderMode === 'PERFORMANCE') return Math.max(0.4, 0.35);
        
        // Slightly dim during interaction to focus on movement
        if (this.hasCooldownActive()) return Math.max(0.8, 0.35);
        
        return Math.max(1.0, 0.35);
    }

    /**
     * ATOM W-010: Get the timestamp of the last recorded interaction.
     */
    public getLastInteractionTimestamp(): number {
        return this.lastInteractionTime;
    }

    /**
     * ATOM W-010: Get the cooldown duration in milliseconds.
     */
    public getCooldownDuration(): number {
        return this.cooldownDuration;
    }

    /**
     * ATOM W-010: Set the cooldown duration (for testing).
     */
    public setCooldownDuration(ms: number): void {
        this.cooldownDuration = ms;
    }

    /**
     * ATOM W-010: Forensic telemetry snapshot of all governance state.
     */
    public getForensicSnapshot(): Record<string, any> {
        return {
            shaderMode: this.shaderMode,
            lastInteractionTime: this.lastInteractionTime,
            cooldownDuration: this.cooldownDuration,
            isFlyToActive: this.isFlyToActive(),
            isInteractionCoolingDown: this.isInteractionCoolingDown(),
            intensityMultiplier: this.getIntensityMultiplier(),
            gesturePriority: this.gesturePriority,
            timestamp: Date.now()
        };
    }
}

export const shaderGovernance = new ShaderGovernance();
